import React, { useState, useEffect, useCallback } from "react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useAI } from "../hooks/useAI";
import SiteFooter from "../components/SiteFooter";
import SiteHeader from "../components/SiteHeader";

// Header and rows must stay on the same track list.
const GRID_COLUMNS = "minmax(0, 1.6fr) minmax(0, 1.2fr) minmax(0, 1.2fr) 1.1fr 1.1fr 0.9fr 0.5fr 0.8fr 32px";

const COLORS = {
  bg: "#F4F4F0",
  bgAlt: "#ede5d6",
  ink: "#1a2330",
  inkSoft: "#4a5568",
  inkMuted: "#6b7280",
  dark: "#0e2030",
  card: "#ffffff",
  border: "rgba(15, 30, 45, 0.08)",
  borderStrong: "rgba(15, 30, 45, 0.16)",
};
const SERIF = "'Playfair Display', 'Source Serif Pro', Georgia, 'Times New Roman', serif";
const SANS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'Fira Code', monospace";

const API_BASE = process.env.REACT_APP_API_URL || "";

const STATUS_STYLES = {
  open:               { label: "Open",               bg: "#e8efea", fg: "#1f6f43", dot: "#2f9b63" },
  awaiting_response:  { label: "Awaiting response",  bg: "#fff3d6", fg: "#8a5a00", dot: "#d4a23a" },
  judged:             { label: "Judged",             bg: "#fdf3d4", fg: "#7a5c00", dot: "#f0c040" },
  resolved:           { label: "Resolved",           bg: "#e3edf7", fg: "#1f4a7a", dot: "#3b78c2" },
  closed:             { label: "Closed",             bg: "#ececec", fg: "#555555", dot: "#888888" },
};

function formatRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

// What the publish switch means for a given dispute, spelled out on hover.
function visibilityTitle(d) {
  if (d.visibility_locked) {
    return `The platform has set this case to ${d.flagged_public ? "public" : "private"} — your own setting is saved but has no effect while that stands.`;
  }
  if (d.effective_public) return "Anyone with the link can read this decision.";
  if (d.is_public) return "This decision page publishes automatically once the panel has ruled.";
  return "Only you and the parties you invited can read this dispute.";
}

export default function AllDisputes() {
  const { user, token, logout } = useAuth();
  const { updateDisputeVisibility } = useAI();
  const navigate = useNavigate();

  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all"); // all | creator | disputant
  const [search, setSearch] = useState("");
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const toggleExpanded = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // Per-dispute state for the publish switch, keyed by dispute id.
  const [visSaving, setVisSaving] = useState({});
  const [visError, setVisError] = useState({});

  const togglePublic = async (dispute, next) => {
    setVisSaving((s) => ({ ...s, [dispute.id]: true }));
    setVisError((s) => { const n = { ...s }; delete n[dispute.id]; return n; });
    const r = await updateDisputeVisibility(dispute.id, next);
    setVisSaving((s) => { const n = { ...s }; delete n[dispute.id]; return n; });
    if (!r.ok) {
      setVisError((s) => ({ ...s, [dispute.id]: r.error || "Could not save" }));
      return;
    }
    // The server resolves the filer's flag against any admin override — mirror
    // what it returns rather than assuming the new value won.
    setDisputes((list) =>
      list.map((x) =>
        x.id === dispute.id
          ? {
              ...x,
              is_public: r.is_public,
              admin_visibility: r.admin_visibility,
              visibility_locked: r.visibility_locked,
              flagged_public: r.flagged_public,
              effective_public: r.effective_public,
            }
          : x
      )
    );
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/ai/disputes/mine`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (res.status === 401) {
        // Stale/invalid token — force re-login so the user isn't stuck.
        logout();
        navigate("/login");
        return;
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setDisputes(data.disputes || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, logout, navigate]);

  useEffect(() => { load(); }, [load]);

  const filtered = disputes.filter((d) => {
    if (filter !== "all" && d.role !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (d.subject || "").toLowerCase().includes(q) ||
        (d.category || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  const counts = {
    all: disputes.length,
    creator: disputes.filter((d) => d.role === "creator").length,
    disputant: disputes.filter((d) => d.role === "disputant").length,
    witness: disputes.filter((d) => d.role === "witness").length,
  };

  return (
    <div style={styles.root}>
      <SiteHeader />

      <main style={styles.main}>
        <div style={styles.pageHeader}>
          <div>
            <h1 style={styles.title}>My disputes</h1>
            <p style={styles.subtitle}>
              {loading ? "Loading…" : `${filtered.length} of ${disputes.length} dispute${disputes.length === 1 ? "" : "s"}`}
            </p>
          </div>
          <div style={styles.toolbar}>
            <div style={styles.searchWrap}>
              <Search size={14} strokeWidth={2} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search subject or category…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={styles.search}
              />
            </div>
            <div style={styles.filterTabs}>
              {[
                { k: "all",         label: "All" },
                { k: "creator",     label: "Started by me" },
                { k: "disputant",   label: "As disputant" },
                { k: "witness", label: "As witness" },
              ].map((t) => (
                <button
                  key={t.k}
                  onClick={() => setFilter(t.k)}
                  style={{
                    ...styles.filterTab,
                    ...(filter === t.k ? styles.filterTabActive : null),
                  }}
                >
                  {t.label}
                  <span style={styles.filterCount}>{counts[t.k]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div style={styles.errorBox}>Failed to load: {error}</div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div style={styles.emptyState}>
            <h3 style={styles.emptyTitle}>No disputes yet</h3>
            <p style={styles.emptyText}>
              {disputes.length === 0
                ? "Start your first dispute from the dashboard."
                : "Nothing matches the current filter."}
            </p>
            {disputes.length === 0 && (
              <button onClick={() => navigate("/submit-dispute")} style={styles.emptyCta}>
                + Create new Dispute
              </button>
            )}
          </div>
        )}

        {filtered.length > 0 && (
          <div style={styles.list} role="table">
            <div style={styles.listHeader} role="row">
              <div style={{ ...styles.cell, ...styles.cellSubject }}>Subject</div>
              <div style={{ ...styles.cell, ...styles.cellDisputant }}>Disputer</div>
              <div style={{ ...styles.cell, ...styles.cellDisputant }}>Disputant</div>
              <div style={{ ...styles.cell, ...styles.cellRole }}>Participants</div>
              <div style={{ ...styles.cell, ...styles.cellStatus }}>Status</div>
              <div style={{ ...styles.cell, ...styles.cellPublic }}>Public</div>
              <div style={{ ...styles.cell, ...styles.cellMeta }}>Turns</div>
              <div style={{ ...styles.cell, ...styles.cellMeta }}>Activity</div>
              <div style={{ ...styles.cell, ...styles.cellAction }}></div>
            </div>

            {filtered.map((d) => {
              const status = STATUS_STYLES[d.dispute_status] || STATUS_STYLES.open;
              const myEmail = (user?.email || "").toLowerCase();
              const isMe = (email) => email && email.toLowerCase() === myEmail;

              const disputerEmail = d.creator_email || null;
              const disputerDisplay = disputerEmail
                ? (isMe(disputerEmail) ? "You" : disputerEmail)
                : "—";

              const disputantDisplay = d.disputant_email
                ? (isMe(d.disputant_email) ? "You" : d.disputant_email)
                : "—";

              const participants = d.participants || [];
              const isExpanded = expandedIds.has(d.id);

              return (
                <React.Fragment key={d.id}>
                  <div
                    onClick={() => navigate(`/dispute/${d.uuid}`)}
                    style={styles.row}
                    role="row"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/dispute/${d.uuid}`);
                      }
                    }}
                    aria-label={`Open dispute: ${d.subject}`}
                  >
                    <div style={{ ...styles.cell, ...styles.cellSubject }}>
                      <div style={styles.subjectMain}>{d.subject || "(no subject)"}</div>
                      {d.category && <div style={styles.subjectMeta}>{d.category}</div>}
                    </div>
                    <div style={{ ...styles.cell, ...styles.cellDisputant }}>
                      <span
                        style={isMe(disputerEmail) ? styles.youBadge : styles.disputantEmail}
                        title={disputerEmail || ""}
                      >
                        {disputerDisplay}
                      </span>
                    </div>
                    <div style={{ ...styles.cell, ...styles.cellDisputant }}>
                      <span
                        style={isMe(d.disputant_email) ? styles.youBadge : styles.disputantEmail}
                        title={d.disputant_email || ""}
                      >
                        {disputantDisplay}
                      </span>
                    </div>
                    <div style={{ ...styles.cell, ...styles.cellRole }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(d.id);
                        }}
                        style={styles.viewBtn}
                        aria-expanded={isExpanded}
                      >
                        <span style={{ ...styles.caret, transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}>▸</span>
                        {isExpanded ? "Hide" : "View"} ({participants.length})
                      </button>
                    </div>
                    <div style={{ ...styles.cell, ...styles.cellStatus }}>
                      <span style={{
                        ...styles.statusBadge,
                        background: status.bg,
                        color: status.fg,
                      }}>
                        <span style={{ ...styles.statusDot, background: status.dot }} />
                        {status.label}
                      </span>
                    </div>
                    {/* Publish switch — only the filer decides, and an admin
                        override (visibility_locked) freezes it. */}
                    <div
                      style={{ ...styles.cell, ...styles.cellPublic }}
                      onClick={(e) => e.stopPropagation()}
                      /* Space/Enter on the switch must toggle it, not trigger
                         the row's "open dispute" handler. */
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      {d.role === "creator" ? (
                          <div style={styles.switchStack}>
                            <label style={styles.switchWrap} title={visibilityTitle(d)}>
                              <input
                                type="checkbox"
                                checked={!!d.is_public}
                                disabled={!!visSaving[d.id] || d.visibility_locked}
                                onChange={(e) => togglePublic(d, e.target.checked)}
                                style={styles.switchInput}
                              />
                              <span
                                style={{
                                  ...styles.switchTrack,
                                  ...(d.is_public ? styles.switchTrackOn : null),
                                  ...(d.visibility_locked ? styles.switchTrackLocked : null),
                                }}
                              >
                                <span
                                  style={{
                                    ...styles.switchKnob,
                                    ...(d.is_public ? styles.switchKnobOn : null),
                                  }}
                                />
                              </span>
                              <span style={styles.switchLabel}>
                                {d.effective_public ? "Public" : d.is_public ? "On" : "Private"}
                              </span>
                            </label>
                            {visError[d.id] && (
                              <span style={styles.switchError}>{visError[d.id]}</span>
                            )}
                          </div>
                        ) : (
                          <span style={styles.publicReadonly}>
                            {d.effective_public ? "Public" : "—"}
                          </span>
                        )}
                    </div>
                    <div style={{ ...styles.cell, ...styles.cellMeta }}>
                      <span style={styles.turnCount}>{d.turn_count}</span>
                    </div>
                    <div style={{ ...styles.cell, ...styles.cellMeta }}>
                      <span style={styles.relative}>{formatRelative(d.last_activity_at)}</span>
                    </div>
                    <div style={{ ...styles.cell, ...styles.cellAction }}>
                      <span style={styles.openArrow} aria-hidden>→</span>
                    </div>
                  </div>
                  <div
                    style={{
                      ...styles.expandedRow,
                      maxHeight: isExpanded ? 600 : 0,
                      opacity: isExpanded ? 1 : 0,
                      paddingTop: isExpanded ? 14 : 0,
                      paddingBottom: isExpanded ? 14 : 0,
                      borderBottom: isExpanded ? `1px solid ${COLORS.border}` : "none",
                    }}
                    aria-hidden={!isExpanded}
                  >
                    <div style={styles.expandedLabel}>Participants ({participants.length})</div>
                    {participants.length === 0 ? (
                      <span style={styles.expandedEmpty}>No participants added yet.</span>
                    ) : (
                      <div style={styles.participantList}>
                        {participants.map((p) => (
                          <span key={p.email} style={styles.participantPill}>
                            <span style={styles.participantRoleTag}>{p.role}</span>
                            <span style={styles.participantEmailText}>
                              {isMe(p.email) ? "You" : p.email}
                            </span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

const styles = {
  // Sticky-footer layout: root is a full-height flex column so the footer
  // sits at the bottom of the viewport (not right under the content) on
  // pages with few/no disputes, instead of leaving a gap below it.
  root: { minHeight: "100vh", display: "flex", flexDirection: "column", background: COLORS.bg, color: COLORS.ink, fontFamily: SANS, fontSize: 14 },

  main: { flex: 1, padding: "32px clamp(16px, 4vw, 48px)", maxWidth: 1280, margin: "0 auto" },

  pageHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-end",
    flexWrap: "wrap", gap: 16, marginBottom: 24,
  },
  title: { fontFamily: SERIF, fontSize: 32, fontWeight: 600, margin: 0, letterSpacing: "-0.5px" },
  subtitle: { fontSize: 13, color: COLORS.inkMuted, margin: "6px 0 0 0" },
  toolbar: { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" },
  searchWrap: { position: "relative" },
  searchIcon: { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: COLORS.inkMuted },
  search: {
    background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8,
    padding: "8px 12px 8px 32px", fontSize: 13, fontFamily: SANS, color: COLORS.ink,
    outline: "none", minWidth: 220,
  },
  filterTabs: { display: "inline-flex", background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 3 },
  filterTab: {
    background: "transparent", border: "none", padding: "6px 12px", fontSize: 12,
    color: COLORS.inkSoft, cursor: "pointer", borderRadius: 6, fontFamily: SANS,
    display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 500,
  },
  filterTabActive: { background: COLORS.dark, color: "#fff" },
  filterCount: {
    fontSize: 10, fontWeight: 700, background: "rgba(0,0,0,0.08)", padding: "1px 6px", borderRadius: 10,
  },

  errorBox: {
    padding: 16, background: "#fef2f2", color: "#991b1b",
    border: "1px solid #fecaca", borderRadius: 8, marginBottom: 16,
  },

  emptyState: {
    textAlign: "center", padding: "60px 24px", background: COLORS.card,
    border: `1px solid ${COLORS.border}`, borderRadius: 12,
  },
  emptyTitle: { fontFamily: SERIF, fontSize: 22, margin: "0 0 8px 0", color: COLORS.ink },
  emptyText: { color: COLORS.inkMuted, margin: "0 0 20px 0", fontSize: 13 },
  emptyCta: {
    background: COLORS.dark, color: "#fff", border: "none", borderRadius: 8,
    padding: "10px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: SANS,
  },

  list: {
    background: COLORS.card, border: `1px solid ${COLORS.border}`,
    borderRadius: 12, overflow: "hidden",
  },
  listHeader: {
    display: "grid",
    gridTemplateColumns: GRID_COLUMNS,
    gap: 16, padding: "12px 20px",
    background: COLORS.bgAlt, color: COLORS.inkMuted,
    fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  row: {
    display: "grid",
    gridTemplateColumns: GRID_COLUMNS,
    gap: 16, padding: "16px 20px",
    background: COLORS.card, color: COLORS.ink, textAlign: "left",
    border: "none", borderBottom: `1px solid ${COLORS.border}`,
    cursor: "pointer", fontFamily: SANS, fontSize: 13,
    transition: "background 0.15s",
    alignItems: "center", width: "100%",
  },
  cell: { minWidth: 0, display: "flex", alignItems: "center" },
  cellSubject: { flexDirection: "column", alignItems: "flex-start", gap: 4 },
  cellDisputant: {},
  cellRole: {},
  cellStatus: {},
  cellMeta: { color: COLORS.inkMuted, fontSize: 12 },
  cellPublic: {},
  cellAction: { justifyContent: "flex-end" },

  // ── Publish switch (filer-only, in the Public column) ────────────────────
  switchStack: { display: "flex", flexDirection: "column", gap: 3, minWidth: 0 },
  switchWrap: {
    display: "inline-flex", alignItems: "center", gap: 8,
    cursor: "pointer", userSelect: "none",
  },
  // The native checkbox stays in the DOM (keyboard + a11y) but is visually
  // replaced by the track/knob below.
  switchInput: {
    position: "absolute", opacity: 0, width: 1, height: 1, margin: 0,
  },
  switchTrack: {
    position: "relative", flexShrink: 0,
    width: 32, height: 18, borderRadius: 999,
    background: "#d5d8dd", transition: "background 0.15s",
  },
  switchTrackOn: { background: "#3b78c2" },
  switchTrackLocked: { opacity: 0.45, cursor: "not-allowed" },
  switchKnob: {
    position: "absolute", top: 2, left: 2,
    width: 14, height: 14, borderRadius: "50%",
    background: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
    transition: "transform 0.15s",
  },
  switchKnobOn: { transform: "translateX(14px)" },
  switchLabel: { fontSize: 12, color: COLORS.inkSoft, whiteSpace: "nowrap" },
  switchError: { fontSize: 10, color: "#991b1b", lineHeight: 1.3 },
  publicReadonly: { fontSize: 12, color: COLORS.inkMuted },

  subjectMain: {
    fontWeight: 600, fontSize: 14, color: COLORS.ink,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    maxWidth: "100%",
  },
  subjectMeta: {
    display: "flex", alignItems: "center", gap: 6, color: COLORS.inkMuted, fontSize: 12,
  },
  disputantEmail: {
    fontFamily: MONO, fontSize: 12, color: COLORS.inkSoft,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    maxWidth: "100%",
  },
  youBadge: {
    fontFamily: SANS, fontSize: 11, fontWeight: 700,
    color: "#fff", background: COLORS.dark,
    padding: "3px 10px", borderRadius: 999, letterSpacing: 0.5,
  },

  roleBadge: {
    padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
    whiteSpace: "nowrap",
  },
  viewBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    background: "transparent", color: COLORS.ink,
    border: `1px solid ${COLORS.border}`, borderRadius: 6,
    padding: "4px 10px", fontSize: 12, fontWeight: 600, fontFamily: SANS,
    cursor: "pointer", transition: "background 0.15s, border-color 0.15s",
  },
  caret: {
    display: "inline-block", fontSize: 10, color: COLORS.inkMuted,
    transition: "transform 0.18s ease",
  },
  expandedRow: {
    background: COLORS.bgAlt,
    paddingLeft: 24, paddingRight: 24,
    overflow: "hidden",
    transition: "max-height 0.22s ease, opacity 0.18s ease, padding 0.18s ease",
  },
  expandedLabel: {
    fontSize: 10, fontWeight: 700, color: COLORS.inkMuted,
    letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8,
  },
  expandedEmpty: { fontSize: 12, color: COLORS.inkMuted, fontStyle: "italic" },
  participantList: { display: "flex", flexWrap: "wrap", gap: 8 },
  participantPill: {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: COLORS.card, border: `1px solid ${COLORS.border}`,
    borderRadius: 999, padding: "4px 10px 4px 4px", fontSize: 12,
  },
  participantRoleTag: {
    fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1,
    background: COLORS.dark, color: "#fff",
    padding: "2px 8px", borderRadius: 999,
  },
  participantEmailText: { fontFamily: MONO, fontSize: 12, color: COLORS.inkSoft },
  statusBadge: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
    whiteSpace: "nowrap",
  },
  statusDot: { width: 6, height: 6, borderRadius: "50%" },
  turnCount: {
    fontFamily: MONO, fontSize: 12, fontWeight: 600, color: COLORS.ink,
    background: COLORS.bgAlt, padding: "2px 8px", borderRadius: 6,
  },
  relative: { fontFamily: MONO, fontSize: 12 },
  openArrow: { color: COLORS.inkMuted, fontSize: 16 },
};
